/**
 * Periods: the display windows that give the chart its non-linear scale. Each
 * one carries its own step, so this panel is where the granularity is decided —
 * and where the A4 tick budget has to be visible while typing, not discovered
 * on submit.
 */

import type { Band, BandUnit } from '../../model/types';
import { MAX_TICKS_PER_BAND, tickCount } from '../../model/validate';
import { field, h, iconButton, numberValue, option } from './dom';

const UNITS: Array<[BandUnit, string]> = [
  ['year', 'année'],
  ['yearBP', 'ans AP'],
  ['MaBP', 'Ma AP'],
];

export interface BandPanelHandlers {
  onSubmit(band: Band, index: number | null): void;
  onDelete(index: number): void;
  onMove(index: number, delta: number): void;
  onSelect(index: number | null): void;
}

export function createBandPanel(handlers: BandPanelHandlers) {
  const label = h('input', { type: 'text', placeholder: 'ex: 16è-17è' });
  const start = h('input', { type: 'number' });
  const end = h('input', { type: 'number' });
  const step = h('input', { type: 'number', min: '1' });
  const unit = h('select', {});
  for (const [value, text] of UNITS) unit.append(option(value, text));
  const pageBreak = h('input', { type: 'checkbox' });

  const submit = h('button', { text: 'Ajouter la période', dataset: { action: 'band-submit' } });
  const cancel = h('button', { text: 'Annuler', hidden: true, dataset: { action: 'band-cancel' } });
  const error = h('div', { class: 'error', hidden: true });
  const summary = h('summary', { text: 'Périodes' });
  const list = h('ul', { class: 'item-list' });

  const budget = h('p', {
    class: 'tip',
    text: `Une période tient sur une page A4 paysage jusqu'à ${MAX_TICKS_PER_BAND} graduations — au-delà, le pas est trop fin pour la durée couverte.`,
  });
  const liveCount = h('span', { class: 'tick-count' });

  const el = h(
    'details',
    { class: 'panel', dataset: { panel: 'bands' } },
    summary,
    budget,
    list,
    h(
      'div',
      { class: 'form-row' },
      field('Libellé', label),
      field('Début', start),
      field('Fin', end),
      field('Pas', step),
      field('Unité', unit),
      field('Saut de page', pageBreak),
      submit,
      cancel,
      liveCount,
    ),
    error,
  );

  let editing: number | null = null;

  /** Draft band from the current inputs, or null when they are not yet a band. */
  const draft = (): Band | null => {
    const from = numberValue(start);
    const to = numberValue(end);
    const by = numberValue(step);
    if (from === null || to === null || by === null || by <= 0 || to <= from) return null;
    return { label: label.value.trim() || '?', start: from, end: to, step: by };
  };

  const updateCount = () => {
    const band = draft();
    if (!band) {
      liveCount.textContent = '';
      liveCount.classList.remove('over');
      return;
    }
    const ticks = tickCount(band);
    liveCount.textContent = `${ticks} graduations / ${MAX_TICKS_PER_BAND}`;
    liveCount.classList.toggle('over', ticks > MAX_TICKS_PER_BAND);
  };

  for (const input of [start, end, step]) input.addEventListener('input', updateCount);

  const read = (): Band | null => {
    error.hidden = true;
    const fail = (message: string): null => {
      error.hidden = false;
      error.textContent = message;
      return null;
    };

    const text = label.value.trim();
    const from = numberValue(start);
    const to = numberValue(end);
    const by = numberValue(step);

    if (text === '') return fail('Le libellé est obligatoire.');
    if (from === null || to === null || by === null) return fail('Début, fin et pas sont obligatoires.');
    if (by <= 0) return fail('Le pas doit être strictement positif.');
    if (to <= from) return fail(`La fin (${to}) doit être postérieure au début (${from}).`);

    const band: Band = { label: text, start: from, end: to, step: by };
    if (unit.value !== 'year') band.unit = unit.value as BandUnit;
    if (pageBreak.checked) band.pageBreakBefore = true;

    const ticks = tickCount(band);
    if (ticks > MAX_TICKS_PER_BAND) {
      return fail(
        `${ticks} graduations pour un maximum de ${MAX_TICKS_PER_BAND} : ` +
          `choisis un pas plus grossier ou découpe cette période.`,
      );
    }

    return band;
  };

  const clear = () => {
    label.value = '';
    for (const input of [start, end, step]) input.value = '';
    unit.value = 'year';
    pageBreak.checked = false;
  };

  const setEditing = (index: number | null, band: Band | null) => {
    editing = index;
    error.hidden = true;
    summary.textContent = band ? `Périodes — modification de « ${band.label} »` : 'Périodes';
    submit.textContent = band ? 'Enregistrer' : 'Ajouter la période';
    cancel.hidden = band === null;
    if (band) el.open = true;

    if (!band) {
      clear();
      updateCount();
      return;
    }
    label.value = band.label;
    start.value = String(band.start);
    end.value = String(band.end);
    step.value = String(band.step);
    unit.value = band.unit ?? 'year';
    pageBreak.checked = Boolean(band.pageBreakBefore);
    updateCount();
  };

  submit.addEventListener('click', () => {
    const band = read();
    if (band) handlers.onSubmit(band, editing);
  });
  cancel.addEventListener('click', () => handlers.onSelect(null));

  return {
    el,

    setBands(bands: readonly Band[], selected: number | null): void {
      list.replaceChildren();

      bands.forEach((band, index) => {
        const ticks = tickCount(band);
        const summaryText = `${band.label} · ${band.start} → ${band.end} · pas ${band.step}`;

        const count = h('em', { class: ticks > MAX_TICKS_PER_BAND ? 'tick-count over' : 'tick-count' });
        count.textContent = `${ticks} grad.`;

        list.append(
          h(
            'li',
            { class: index === selected ? 'selected' : '' },
            h('span', { text: summaryText }),
            count,
            iconButton('up', 'Monter', () => handlers.onMove(index, -1), { disabled: index === 0 }),
            iconButton('down', 'Descendre', () => handlers.onMove(index, 1), {
              disabled: index === bands.length - 1,
            }),
            iconButton('edit', 'Modifier', () => handlers.onSelect(index)),
            iconButton('delete', 'Supprimer', () => handlers.onDelete(index), { class: 'danger' }),
          ),
        );
      });

      if (bands.length === 0) {
        list.append(h('li', { class: 'empty', text: 'Aucune période — la frise ne peut rien afficher.' }));
      }

      setEditing(selected, selected === null ? null : (bands[selected] ?? null));
    },
  };
}
